import { useMemo } from "react";

import { type BuilderInfo } from "helpers/AppProvider.types";

import { getEntityMeta } from "commonComponents/utilities/MainNavigation/AiSearch/entityMeta";
import { useEntityNavigation } from "commonComponents/utilities/MainNavigation/AiSearch/hooks/useEntityNavigation";
import type { ISearchHit } from "commonComponents/utilities/MainNavigation/AiSearch/types";

import { type ISearchDialogSection, SearchDialogList } from "../rows/SearchDialogList";
import type { ISearchDialogRow } from "../rows/searchDialogRow.types";
import type { NavMethod } from "../searchDialog.learn";
import { getRecentItems, recentToHit } from "./recentItems";
import { getTryAskingPrompts } from "./tryAsking";

export interface IUseBrowseSectionsArgs {
    userId: number;
    builderInfo: BuilderInfo | null | undefined;
    /** Entity kind of the record currently open, used to pick prompts. */
    openRecordEntity: string | null;
    /**
     * The prompt's index and the list length ride along for telemetry: the
     * prompts are a fixed curated list, so the index identifies one exactly
     * and the text never has to be logged.
     */
    onAskPrompt: (prompt: string, promptIndex: number, promptCount: number) => void;
    /**
     * Called once a recent has navigated out from under the dialog, so it can
     * close and record the open. `rank` is the 1-based row within Recent.
     */
    onRecentOpened: (hit: ISearchHit, rank: number, navMethod: NavMethod) => void;
}

/**
 * The rows shown with no query typed: recents from local storage and prompts
 * from a static list, so browse needs no network.
 */
export function useBrowseSections(args: IUseBrowseSectionsArgs): ISearchDialogSection[] {
    const { userId, builderInfo, openRecordEntity, onAskPrompt, onRecentOpened } = args;
    const { isRoutable, open } = useEntityNavigation(builderInfo);

    return useMemo<ISearchDialogSection[]>(() => {
        const recentRows: ISearchDialogRow[] = getRecentItems(userId)
            .map((item) => ({ item, hit: recentToHit(item) }))
            // A stored record can stop being routable; dropping it beats a
            // row that does nothing when clicked.
            .filter(({ hit }) => isRoutable(hit))
            .map(({ item, hit }, index) => ({
                key: `recent:${item.entity}:${item.id}`,
                kind: "record" as const,
                icon: getEntityMeta(item.entity).icon,
                title: item.title,
                subtitle: item.subtitle,
                amount: null,
                status: null,
                trailingLabel: getEntityMeta(item.entity).label,
                onSelect: (navMethod = "click") => {
                    open(hit);
                    // A relative entity opens as a /SearchResult/* modal, which
                    // would otherwise render behind this one.
                    onRecentOpened(hit, index + 1, navMethod);
                },
            }));

        const promptRows: ISearchDialogRow[] = getTryAskingPrompts({ openRecordEntity }).map(
            (prompt, index, all) => ({
                key: `prompt:${prompt}`,
                kind: "prompt" as const,
                icon: null,
                title: prompt,
                subtitle: null,
                amount: null,
                status: null,
                trailingLabel: null,
                onSelect: () => onAskPrompt(prompt, index, all.length),
            }),
        );

        return [
            { label: "Recent", rows: recentRows },
            { label: "Try asking", rows: promptRows },
        ];
    }, [userId, openRecordEntity, isRoutable, open, onAskPrompt, onRecentOpened]);
}

export interface IBrowseViewProps {
    sections: ISearchDialogSection[];
    highlightedIndex: number;
    onHoverRow: (index: number) => void;
}

/** The dialog with no query typed: what the user might want before they say. */
export function BrowseView({ sections, highlightedIndex, onHoverRow }: IBrowseViewProps) {
    return (
        <SearchDialogList
            sections={sections}
            highlightedIndex={highlightedIndex}
            onHoverRow={onHoverRow}
        />
    );
}
